import React, { useState, useEffect } from "react";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  fetchLeadDataById,
  fetchAllLeadStatus,
  UpdateLeadStatus,
  GetLeadDynamicData,
  fetchReasons,
} from "../Services/APIService";
import selectStyles from "../js/selectStyles.js";

export interface LeadDynamicData {
  createdOn: string;
  leadAge: number;
  followUpAt: string;
  followUpName: string;
  initialAssignUser: number;
  currentAssignUser: number;
  initialUserName: string;
  currentUserName: string;
  projectNames: string;
  errorMessage: string;
}

interface LeadDetailsById {
  leadId: number;
  firstName: string;
  lastName: string;
  gender: string;
  dob: string;
  dom: string;
  mobile: string;
  countryCode: string;

  alternateMobile: string;
  alternateMobileCountryCode: string;
  email: string;
  address: string;
  stateId: number;
  stateName: string;
  districtId: number;
  districtName: string;
  cityId: number;
  cityName: string;
  zipCode: number;
  sourceId: number;
  sourceName: string;
  subSourceId: number;
  subSourceName: string;
  currentStatus: number;
  currentStatusName: string;
  incomeFrom: number;
  incomeTo: number;
  budgetMin: number;
  budgetMax: number;
  initialAssignedUser: number;
  initialAssignedUserName: string;
  currentAssignedUser: number;
  currentAssignedUserName: string;
}

interface SelectOption {
  value: number;
  label: string;
}

interface LeadDetailsViewProps {
  leadId: number;
  onTaskCreated: () => void;
}

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);

  if (isNaN(date.getTime())) return "N/A";

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  const hourStr = String(hours).padStart(2, "0");

  return `${day}-${month}-${year} ${hourStr}:${minutes} ${ampm}`;
};

const LeadDashboardView: React.FC<LeadDetailsViewProps> = ({
  leadId,
  onTaskCreated,
}) => {
  const [leadDetails, setLeadDetails] = useState<LeadDetailsById>({
    leadId: 0,
    firstName: "",
    lastName: "",
    gender: "",
    dob: "",
    dom: "",
    mobile: "",
    countryCode: "",
    alternateMobile: "",
    alternateMobileCountryCode: "",

    email: "",
    address: "",
    stateId: 0,
    stateName: "",
    districtId: 0,
    districtName: "",
    cityId: 0,
    cityName: "",
    zipCode: 0,
    sourceId: 0,
    sourceName: "",
    subSourceId: 0,
    subSourceName: "",
    currentStatus: 0,
    currentStatusName: "",
    incomeFrom: 0,
    incomeTo: 0,
    budgetMin: 0,
    budgetMax: 0,
    initialAssignedUser: 0,
    initialAssignedUserName: "",
    currentAssignedUser: 0,
    currentAssignedUserName: "",
  });

  const [leadStatusOptions, setLeadStatusOptions] = useState<SelectOption[]>(
    []
  );
  const [selectedStatus, setSelectedStatus] = useState<SelectOption | null>(
    null
  );
  const [pendingStatus, setPendingStatus] = useState<SelectOption | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("details");
  const [isSaving, setIsSaving] = useState(false);
  const [flatNo, setFlatNo] = useState("");
  const [bookingAmount, setBookingAmount] = useState("");
  const [reasonOptions, setReasonOptions] = useState<SelectOption[]>([]);
  const [selectedReason, setSelectedReason] = useState<SelectOption | null>(null);
  const [statusChanged, setStatusChanged] = useState(false);
  const navigate = useNavigate();
  const getReasonType = (typeId: number): string => {
    const types: Record<number, string> = {
      1: "Lead Lost Reason",
      2: "Booking Cancel Reason",
      3: "Type C",
    };

    return types[typeId] ?? `Type ${typeId}`;
  };
  const [leadDynamicData, setLeadDynamicData] =
    useState<LeadDynamicData | null>(null);

  // Fetch Lead by ID
  useEffect(() => {
    const fetchLeadDataByIdData = async (): Promise<void> => {
      try {
        const leaddetailsbyidservice = await fetchLeadDataById(leadId);
        setLeadDetails(leaddetailsbyidservice);
        setLoading(false);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
        setLoading(false);
      }
    };

    fetchLeadDataByIdData();
  }, [leadId]);

  // Fetch Dynamic Lead Data
  useEffect(() => {
    const fetchDynamicLeadData = async (): Promise<void> => {
      try {
        const data = await GetLeadDynamicData(leadId);
        console.log("Dynamic Lead Data:", data);
        setLeadDynamicData(data);
      } catch (error) {
        console.error("Error fetching lead dynamic data:", error);
      }
    };

    fetchDynamicLeadData();
  }, [leadId]);

  // Fetch all lead statuses
  useEffect(() => {
    const fetchAllLeadStatusData = async (): Promise<void> => {
      try {
        const allleadstatusservice = await fetchAllLeadStatus();

        const options = allleadstatusservice.leadStatusList.map((status) => ({
          value: status.statusId,
          label: status.statusName,
        }));

        setLeadStatusOptions(options);

        const initialStatus = options.find(
          (option) => option.value === leadDetails.currentStatus
        );
        setSelectedStatus(initialStatus || null);
        setPendingStatus(initialStatus || null);

        setLoading(false);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
        setLoading(false);
      }
    };

    fetchAllLeadStatusData();
  }, [leadDetails.currentStatus]);


  useEffect(() => {
    const loadReasons = async () => {
      try {
        const tenantId = Number(localStorage.getItem("tenantId"));

        const data = await fetchReasons(tenantId);

        let filteredReasons = data;

        if (pendingStatus?.value === 9) {
          // Lost
          filteredReasons = data.filter((item: any) => item.typE_ID === 1);
        }

        if (pendingStatus?.value === 8) {
          // Cancel
          filteredReasons = data.filter((item: any) => item.typE_ID === 2);
        }

        const options = filteredReasons.map((item: any) => ({
          value: item.id,
          label: item.description,
        }));

        setReasonOptions(options);
      } catch (error) {
        console.error("Failed to fetch reasons", error);
        toast.error("Failed to load reasons");
      }
    };

    if (pendingStatus?.value === 8 || pendingStatus?.value === 9) {
      loadReasons();
    }
  }, [pendingStatus]);


  const handleStatusChange = (selectedOption: SelectOption | null) => {
    setPendingStatus(selectedOption);
    setStatusChanged(true);
  };
  const handleSaveStatus = () => {

    if (!pendingStatus) {
      toast.error("Please select a status");
      return;
    }

    if (isLost || isCancel) {
      if (!selectedReason) {
        toast.error("Please select reason");
        return;
      }
    }

    toast(
      (t) => (
        <div>
          <p className="mb-2">Do you want to update the status?</p>
          <div className="d-flex gap-2">
            <button
              className="btn btn-sm btn-primary"
              onClick={() => {
                toast.dismiss(t.id);
                updateStatus();
              }}
            >
              Yes
            </button>

            <button
              className="btn btn-sm btn-secondary"
              onClick={() => toast.dismiss(t.id)}
            >
              Cancel
            </button>
          </div>
        </div>
      ),
      { duration: 5000 }
    );
  };

  const updateStatus = async () => {

    if (!pendingStatus) return;

    setIsSaving(true);

    const newStatus = pendingStatus.value;
    const newStatusName = pendingStatus.label;

    const tenantId = localStorage.getItem("tenantId");
    const userId = localStorage.getItem("UserId");

    try {

      const payload = {
        leadId: leadDetails.leadId,
        tenantId: tenantId ? parseInt(tenantId) : 0,
        currentStatus: newStatus,
        modifiedBy: userId ? parseInt(userId) : 0,
        reasonId: (isLost || isCancel) ? selectedReason?.value ?? null : null
      };

      console.log("Update Status Payload:", payload);

      const response = await UpdateLeadStatus(payload);

      if (response && response.isUpdated) {

        setLeadDetails({
          ...leadDetails,
          currentStatus: newStatus,
          currentStatusName: newStatusName
        });

        setSelectedStatus(pendingStatus);
        setStatusChanged(false);

        toast.success("Lead status updated successfully!");
        onTaskCreated();
      }

    } catch (err) {

      const errMsg =
        err instanceof Error ? err.message : "Failed to update lead status";

      setError(errMsg);

      toast.error(errMsg);

      setPendingStatus(selectedStatus);

    } finally {

      setIsSaving(false);

    }

  };

  const redirectToLeadEdit = (leadId: number) => {
    navigate("/leadView", {
      state: { editLeadId: leadId },
    });
  };

  const handleEditClick = async (leadId: number) => {
    try {
      const response = await fetchLeadDataById(leadId);
      if (response) {
        navigate("/editLead", {
          state: { LeadData: response, redirectTo: "/leaddetails" },
        });
      } else {
        throw new Error("No lead data found");
      }
    } catch (error) {
      console.error("Error fetching lead edit data:", error);
      toast.error("Failed to fetch lead data for editing");
    }
  };

  const firstInitial = leadDetails.firstName.charAt(0);
  const lastInitial = leadDetails.lastName
    ? leadDetails.lastName.charAt(0)
    : "";
  const initials = `${firstInitial}${lastInitial}`.toUpperCase();

  // Check if status has changed
  const hasStatusChanged = pendingStatus?.value !== selectedStatus?.value;
  const isCancel = pendingStatus?.value === 8;
  const isLost = pendingStatus?.value === 9;
  // Render Details Tab Content
  const renderDetailsTab = () => (
    <div className="row">
      <div className="col-md-2">
        <div className="form-label fw-bold">Received On</div>
        <div className="mt-1 font-sizes">
          {leadDynamicData?.createdOn
            ? formatDate(leadDynamicData.createdOn)
            : "N/A"}
        </div>
      </div>
      <div className="col-md-2">
        <div>
          <div className="form-label fw-bold">Lead Age</div>
          <div className="mt-1 font-sizes">{leadDynamicData?.leadAge} days</div>
        </div>
      </div>
      <div className="col-md-2">
        <div>
          <div className="form-label fw-bold">Last Followup Date</div>
          <div className="mt-1 font-sizes">
            {leadDynamicData?.followUpAt
              ? formatDate(leadDynamicData.followUpAt)
              : "N/A"}
          </div>
        </div>
      </div>
      <div className="col-md-2">
        <div>
          <div className="form-label fw-bold">Owner</div>
          <div className="mt-1 font-sizes">
            {leadDynamicData?.currentUserName || "-"}
          </div>
        </div>
      </div>
      <div className="col-md-2">
        <div>
          <div className="form-label fw-bold">Last Followup</div>
          <div className="mt-1 font-sizes">{leadDynamicData?.followUpName}</div>
        </div>
      </div>
      <div className="col-md-2">
        <div>
          <div className="form-label fw-bold">Interested Projects</div>
          <div className="mt-1 font-sizes">
            {leadDynamicData?.projectNames || "-"}
          </div>
        </div>
      </div>
      <div className="col-md-2 mt-4">
        <div>
          <div className="form-label fw-bold">Source</div>
          <div className="mt-1 font-sizes">{leadDetails.sourceName || "-"}</div>
        </div>
      </div>
      <div className="col-md-2 mt-4">
        <div>
          <div className="form-label fw-bold">Sub Source</div>
          <div className="mt-1 font-sizes">
            {leadDetails.subSourceName || "-"}
          </div>
        </div>
      </div>
      <div className="col-md-2 mt-4">
        <div>
          <div className="form-label fw-bold">Alternate Number</div>
          <div className="mt-1 font-sizes">
            {leadDetails.alternateMobileCountryCode || ""}{leadDetails.alternateMobile || "-"}
          </div>
        </div>
      </div>
    </div>
  );

  // Render Profile Details Tab Content
  const renderProfileDetailsTab = () => (
    <div className="profile-details">
      <div className="row">
        <div className="col-md-2">
          <div>
            <div className="form-label fw-bold">Income From</div>
            <div className="mt-1 font-sizes">
              {leadDetails.incomeFrom
                ? `₹${leadDetails.incomeFrom.toLocaleString()}`
                : "-"}
            </div>
          </div>
        </div>
        <div className="col-md-2">
          <div>
            <div className="form-label fw-bold">Income To</div>
            <div className="mt-1 font-sizes">
              {leadDetails.incomeTo
                ? `₹${leadDetails.incomeTo.toLocaleString()}`
                : "-"}
            </div>
          </div>
        </div>
        <div className="col-md-2">
          <div>
            <div className="form-label fw-bold">Budget Min</div>
            <div className="mt-1 font-sizes">
              {leadDetails.budgetMin
                ? `₹${leadDetails.budgetMin.toLocaleString()}`
                : "-"}
            </div>
          </div>
        </div>
        <div className="col-md-2">
          <div>
            <div className="form-label fw-bold">Budget Max</div>
            <div className="mt-1 font-sizes">
              {leadDetails.budgetMax
                ? `₹${leadDetails.budgetMax.toLocaleString()}`
                : "-"}
            </div>
          </div>
        </div>
        <div className="col-md-2">
          <div>
            <div className="form-label fw-bold">Gender</div>
            <div className="mt-1 font-sizes">
              {leadDetails.gender === "M"
                ? "Male"
                : leadDetails.gender === "F"
                  ? "Female"
                  : leadDetails.gender === "O"
                    ? "Other"
                    : "-"}
            </div>
          </div>
        </div>
        <div className="col-md-2">
          <div>
            <div className="form-label fw-bold">State</div>
            <div className="mt-1 font-sizes">
              {leadDetails.stateName || "-"}
            </div>
          </div>
        </div>
        <div className="col-md-2 mt-4">
          <div>
            <div className="form-label fw-bold">District</div>
            <div className="mt-1 font-sizes">
              {leadDetails.districtName || "-"}
            </div>
          </div>
        </div>
        <div className="col-md-2 mt-4">
          <div>
            <div className="form-label fw-bold">City</div>
            <div className="mt-1 font-sizes">{leadDetails.cityName || "-"}</div>
          </div>
        </div>
        <div className="col-md-2 mt-4">
          <div>
            <div className="form-label fw-bold">Zip Code</div>
            <div className="mt-1 font-sizes">{leadDetails.zipCode || "-"}</div>
          </div>
        </div>
        <div className="col-md-2 mt-4">
          <div>
            <div className="form-label fw-bold">Address</div>
            <div className="mt-1 font-sizes">{leadDetails.address || "-"}</div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="card">
      <div className="card-header carHeaderWithBtn premium-header">
        <div className="d-flex justify-content-between align-items-center w-100">

          {/* Left Section - Name */}
          <div>
            <h5 className="card-title mb-0 lead-name">
              {`${leadDetails.firstName ?? ""} ${leadDetails.lastName ?? ""
                }`.trim()}
            </h5>

          </div>

          {/* Right Section - Lead ID Badge */}
          <div className="lead-id-badge">
            ID: {leadDetails.leadId}
          </div>

        </div>
      </div>

      <div className="card-body">
        <div className="lead-details">
          <div className="row align-items-center mb-3">
            <div className="col-8">
              <div className="contact-container">
                <span className="initials-circle-container">
                  <span className="initials-circle">{initials}</span>
                </span>
                <div className="mt-3">
                  {" "}
                  <div className="contact-info">{leadDetails.countryCode} {leadDetails.mobile?.slice(-10)}</div>
                  <div className="contact-info mt-1">{leadDetails.email}</div>
                </div>
              </div>
            </div>
            <div className="col-4 mt-2">
              <label className="form-label fw-bold">Status</label>

              <div className="d-flex gap-2 align-items-start">
                <div style={{ flex: 1 }}>
                  <Select
                    options={leadStatusOptions}
                    value={pendingStatus}
                    onChange={handleStatusChange}
                    className="basic-single-select"
                    classNamePrefix="select"
                    placeholder="Select status"
                    isLoading={loading}
                    isClearable
                    noOptionsMessage={() => "No menu items available"}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={selectStyles}
                  />
                </div>

                <button
                  className="btn btn-primary"
                  onClick={handleSaveStatus}
                  disabled={!hasStatusChanged || isSaving}
                  style={{ whiteSpace: "nowrap" }}
                >
                  {isSaving ? "Saving..." : "Save"}
                </button>
              </div>



              {/* Lost / Cancel Reason */}
              {statusChanged && (isLost || isCancel) && (
                <div className="mt-2">
                  <label className="form-label">Reason *</label>

                  <Select
                    options={reasonOptions}
                    value={selectedReason}
                    onChange={(option) => setSelectedReason(option)}
                    placeholder="Select reason"
                    styles={selectStyles}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="leadDeatils-main-div">
            {/* Tab Navigation */}
            <ul
              className="nav nav-tabs cardHeaderHegiht d-flex"
              id="myTab"
              role="tablist"
              style={{ borderBottom: "1px solid #dee2e6" }}
            >
              {["details", "profile"].map((tab) => (
                <li className="nav-item" role="presentation" key={tab}>
                  <button
                    className={`nav-link card-title tab-btn cardHeaderHegiht followUpcardHeaddashboard ${activeTab === tab ? "active" : ""
                      }`}
                    id={`tab-${tab}`}
                    onClick={() => setActiveTab(tab)}
                    type="button"
                    role="tab"
                    aria-controls={`tab-${tab}-pane`}
                    aria-selected={activeTab === tab}
                  >
                    {tab === "details" ? "View Details" : "Profile Details"}
                  </button>
                </li>
              ))}
            </ul>

            {/* Tab Content */}
            <div className="tab-content" id="myTabContent">
              <div
                className={`tab-pane fade leadDashboard-deatils-tab ${activeTab === "details" ? "show active" : ""
                  }`}
                id="tab-details-pane"
              >
                {renderDetailsTab()}
              </div>
              <div
                className={`tab-pane fade leadDashboard-deatils-tab ${activeTab === "profile" ? "show active" : ""
                  }`}
                id="tab-profile-pane"
              >
                {renderProfileDetailsTab()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadDashboardView;
